// client/src/reservations/pages/BookReservationPage.jsx
import React, { useEffect, useMemo, useState } from 'react';
import { useParams, useNavigate, useSearchParams } from 'react-router-dom';
import {
  ensureCart,
  getBootstrap,
  addItem,
  getCart,
  createHolds,
  releaseHolds,
} from '../api/reservationsApi';
import { computeQuote } from '../utils/fees';
import TimeFiltersBar from '../components/TimeFiltersBar';
import OfferingCard from '../components/OfferingCard';
import TimeResultsGrid from '../components/TimeResultsGrid';
import CartSummary from '../components/CartSummary';
import RecurringPicker from '../components/RecurringPicker';
import WaitlistOptIn from '../components/WaitlistOptIn';
import useBusinessSocket from '../hooks/useBusinessSocket';
import { msOpenDates } from '../utils/hours';
import { cbSessionsLocal, subtractWindows} from '../utils/availabilityLocal'
import { msSlotsClient } from '../utils/msClient';
import { getStripeConfig, startPayment } from '../api/reservationsApi';

export default function BookReservationPage() {
  const { businessId } = useParams();
  const navigate = useNavigate();
  const [sp] = useSearchParams();
  const expiredMsg = sp.get('expired') === '1';
  const [bootstrap, setBootstrap] = useState(null);
  const [seedLive, setSeedLive] = useState({});
  const live = useBusinessSocket(businessId, seedLive);

  const [tab, setTab] = useState('offerings'); // 'offerings' | 'time'
  const [cart, setCart] = useState(null);

 // time filters
  const [queryDraft, setQueryDraft] = useState({
    day_from: null, day_to: null,
    win_start: '09:00', win_end: '22:00',
    party: 2,
    category: '',           // NEW
    ac_exact: true          // NEW: rooms exact dates?
  });
  const [applied, setApplied] = useState({
    day_from: null, day_to: null,
    win_start: '09:00', win_end: '22:00',
    party: 2,
    category: '',
    ac_exact: true
  });

  const [offerings, setOfferings] = useState([]);
  const [quote, setQuote] = useState({ subtotal: 0, pay_now: 0, hold_amount: 0 });
  const [cartItems, setCartItems] = useState([]);
  const pendingAddsRef = React.useRef(new Set()); // dedupe while an add is in flight
  const [timeFocus, setTimeFocus] = useState(null); // { id, kind } | null
  // project cart items into "live-like" blocks so local availability excludes them
  const liveFromCart = React.useMemo(() => {
    const out = { ps_service: [], cb_class: [], ms_table_type: [], ac_room_type: [] };
    for (const it of (cartItems || [])) {
      const party = Number(it?.guests_json?.party_size ?? 1);
      if (it.offering_kind === 'ps_service' && it.slot_start && it.slot_end) {
        out.ps_service.push({
          offering_id: it.offering_id,
          employee_id: it.employee_id,
          slot_start: it.guests_json?.slot_start_local || it.slot_start,
          slot_end:   it.guests_json?.slot_end_local   || it.slot_end,
          party_size: party,
          source: 'cart',
          cart_id: cart?.id,
        });
      } else if (it.offering_kind === 'cb_class') {
        out.cb_class.push({
          offering_id: it.offering_id,
          class_session_id: it.guests_json?.class_session_id,
          class_session_date: it.guests_json?.class_session_date,
          slot_start: it.guests_json?.slot_start_local || it.slot_start,
          slot_end:   it.guests_json?.slot_end_local   || it.slot_end,
          party_size: party,
          source: 'cart',
          cart_id: cart?.id,
        });
      } else if (it.offering_kind === 'ms_table_type' && it.slot_start && it.slot_end) {
        out.ms_table_type.push({
          offering_id: it.offering_id,
          ms_seating_config_id: it.guests_json?.ms_seating_config_id || it.ms_seating_config_id,
          ms_area_type:         it.guests_json?.ms_area_type         || it.ms_area_type,
          slot_start: it.guests_json?.slot_start_local || it.slot_start,
          slot_end:   it.guests_json?.slot_end_local   || it.slot_end,
          party_size: party,
          source: 'cart',
          cart_id: cart?.id,
        });
      } else if (it.offering_kind === 'ac_room_type') {
        const s = it?.guests_json?.ac_start_date;
        const e = it?.guests_json?.ac_end_date;
        if (s && e) out.ac_room_type.push({
          offering_id: it.offering_id,
          nights: `["${s}","${e}")`,
          party_size: Number(it?.guests_json?.party_size ?? 1),
          source: 'cart',
          cart_id: cart?.id,
        });
      }
    }
    return out;
  }, [cartItems, cart?.id]);

  const liveWithCart = React.useMemo(() => ({
    ps_service:   [ ...(live?.ps_service||[]),   ...(liveFromCart.ps_service||[]) ],
    cb_class:     [ ...(live?.cb_class||[]),     ...(liveFromCart.cb_class||[]) ],
    ms_table_type:[ ...(live?.ms_table_type||[]),...(liveFromCart.ms_table_type||[]) ],
    ac_room_type: [ ...(live?.ac_room_type||[]), ...(liveFromCart.ac_room_type||[]) ],
  }), [live, liveFromCart]);
  // optional steps
  const [preorder, setPreorder] = useState({ allow: false, products: [], picked: [] });
  const [recurring, setRecurring] = useState({ enabled: false, freq: 'weekly', count: 4 });
  const [waitlist, setWaitlist] = useState({ enabled: false });
const includeDatesForTimeTab = React.useMemo(() => {
  const hoursCtx = bootstrap?.calendars?.hours;
  const range = bootstrap?.meta?.booking?.range;
  if (!hoursCtx || !range) return [];
  if (timeFocus?.kind === 'ms_table_type') {
    const o = offerings.find(x => x.kind === 'ms_table_type' && x.id === timeFocus.id);
    if (!o || !range?.start_date || !range?.end_date) return [];
    const caps = (bootstrap?.calendars?.ms_rules?.capacity || [])
      .filter(r => String(r.seating_config_id) === String(o.ms_seating_config_id));
    const hasCaps = caps.length > 0;
    const maxTotal = hasCaps
      ? caps.reduce((m, r) => Math.max(m, Number(r.total_size || 0)), 0)
      : Infinity;
    if (hasCaps && Number(queryDraft?.party || 0) > maxTotal) return []; // disable only when defined
    const out = [];
    const start = new Date(range.start_date);
    const end   = new Date(range.end_date);
    for (let d = new Date(range.start_date); d <= new Date(range.end_date); d.setDate(d.getDate() + 1)) {
      const iso = d.toISOString().slice(0,10);
      const any = msSlotsClient({
        dateISO: iso,
        party: queryDraft.party,
        seatingConfigId: o.ms_seating_config_id,
        areaType: o.ms_area_type,
        hoursCtx,
        msRules: bootstrap?.calendars?.ms_rules,
        live: liveWithCart,
        firstBookableISO: bootstrap?.meta?.booking?.first_bookable_ts,
        tz: bootstrap?.meta?.timezone
      }).length > 0;
      if (any) out.push(iso);
    }
    return out;
  }
  if (timeFocus?.kind === 'ps_service') {
    const o = offerings.find(x => x.kind === 'ps_service' && x.id === timeFocus.id);
    return o?._availableDates || [];
  }
  if (timeFocus?.kind === 'cb_class') {
    const sessions = bootstrap?.calendars?.cb_sessions || [];
    const cls = offerings.find(x => x.kind === 'cb_class' && x.id === timeFocus.id);
    if (!cls) return [];
    const out = [];
    const start = new Date(range.start_date);
    const end   = new Date(range.end_date);
    const need  = Math.max(1, Number(queryDraft?.party || 1)); // reflect current draft party
    for (let d = new Date(start); d <= end; d.setDate(d.getDate()+1)) {
      const iso = d.toISOString().slice(0,10);
      const r = cbSessionsLocal({
        dateISO: iso,
        classId: cls.id,
        capacity: cls.capacity,
        cbSessions: sessions,
        tz: bootstrap?.meta?.timezone,
        firstBookableISO: bootstrap?.meta?.booking?.first_bookable_ts,
        live: liveWithCart,
      });
      if ((r.sessions || []).some(s => s.remaining >= need)) out.push(iso);
    }
    return out;
  }
  if (timeFocus?.kind === 'ac_room_type') {
    // only priced season days within horizon
    const seasons = bootstrap?.calendars?.ac_pricing?.seasons || [];
    const rt = offerings.find(x => x.kind === 'ac_room_type' && x.id === timeFocus.id);
    if (!rt) return [];
    const set = new Set();
    const clipStart = new Date(range.start_date);
    const clipEnd   = new Date(range.end_date);
    seasons.filter(s => s.room_type_id === rt.id).forEach(s => {
      const a = new Date(s.start_date), b = new Date(s.end_date);
      const from = a < clipStart ? clipStart : a;
      const to   = b > clipEnd   ? clipEnd   : b;
      for (let d = new Date(from); d <= to; d.setDate(d.getDate()+1)) {
        set.add(d.toISOString().slice(0,10));
      }
    });
    return Array.from(set).sort();
  }
  const out = new Set();
 
  // PS: we already computed per-service bookable dates
  offerings
    .filter(o => o.kind === 'ps_service')
    .forEach(o => (o._availableDates || []).forEach(d => out.add(d)));
 
  // CB: walk horizon; if any class has remaining seats on a day, include it
  const need = Math.max(1, Number(queryDraft?.party || 1));
  const cbSessions = bootstrap?.calendars?.cb_sessions || [];
  const start = new Date(range.start_date);
  const end   = new Date(range.end_date);
  for (let d = new Date(start); d <= end; d.setDate(d.getDate()+1)) {
    const iso = d.toISOString().slice(0,10);
    const anyClass = offerings
      .filter(o => o.kind === 'cb_class')
      .some(o => {
        const r = cbSessionsLocal({
          dateISO: iso,
          classId: o.id,
          capacity: o.capacity,
          cbSessions,
          tz: bootstrap?.meta?.timezone,
          firstBookableISO: bootstrap?.meta?.booking?.first_bookable_ts,
          live: liveWithCart,
        });
        return (r.sessions || []).some(s => s.remaining >= need);
      });
    if (anyClass) out.add(iso);
  }
 
  // MS: union of open dates for all seating configs
  const msIds = offerings
    .filter(x => x.kind === 'ms_table_type')
    .map(x => x.ms_seating_config_id);
  msIds.forEach(id => msOpenDates(hoursCtx, range, id).forEach(d => out.add(d)));
 
  // AC: union of priced days in season windows
  const seasons = bootstrap?.calendars?.ac_pricing?.seasons || [];
  const clipStart = new Date(range.start_date);
  const clipEnd   = new Date(range.end_date);
  offerings
    .filter(o => o.kind === 'ac_room_type')
    .forEach(rt => {
      seasons.filter(s => String(s.room_type_id) === String(rt.id)).forEach(s => {
        const a = new Date(s.start_date), b = new Date(s.end_date);
        const from = a < clipStart ? clipStart : a;
        const to   = b > clipEnd   ? clipEnd   : b;
        for (let d = new Date(from); d <= to; d.setDate(d.getDate()+1)) {
          out.add(d.toISOString().slice(0,10));
        }
      });
    });
 
  return Array.from(out).sort();
}, [
  bootstrap?.calendars?.hours,
  bootstrap?.meta?.booking?.range,
  bootstrap?.calendars?.cb_sessions,
  bootstrap?.calendars?.ac_pricing?.seasons,
  offerings,
  timeFocus?.id,
  timeFocus?.kind,
  liveWithCart,
  queryDraft?.party
]);
console.log('includeDatesForTimeTab', includeDatesForTimeTab);
  const employeeIndex = React.useMemo(() => {
    const map = {};
    (bootstrap?.calendars?.ps?.employees || []).forEach(row => {
      // employees table likely shared across PS/CB; photo_url may be missing — that's OK (we’ll fallback to initials)
      map[row.employee_id] = { id: row.employee_id, name: row.employee_name, photo_url: row.photo_url || null };
    });
    return map;
  }, [bootstrap?.calendars?.ps?.employees]);

  const psScheduleMode = React.useMemo(() => {
    const rows = bootstrap?.calendars?.ps?.schedule_mode || [];
    const m = {};
    rows.forEach(r => {
      m[r.service_id] = String(r.mode || 'business').trim().toLowerCase();
    });
    return m;
  }, [bootstrap?.calendars?.ps?.schedule_mode]);

  async function reloadCartItems(id = cart?.id) {
    if (!id) return;
    const c = await getCart(id);
    setCartItems(c.items || []);
  }

function buildOfferingsFromBootstrap(boot) {
  if (!boot) return [];
  const { offerings, discounts, meta } = boot;
  const currency = meta?.base_currency || 'EUR';
  const acSeasons = boot?.calendars?.ac_pricing?.seasons || [];

  const minSeasonPrice = (roomTypeId) => {
    const rows = acSeasons.filter(s => String(s.room_type_id) === String(roomTypeId));
    if (!rows.length) return 0;
    return rows.reduce((m, r) => {
      const v = Number(r.price_amount || 0);
      return (m == null) ? v : Math.min(m, v);
    }, null) ?? 0;
  };
  const fromPS = (offerings?.ps_services || []).map(r => decorateWithDiscounts({
    business_id: meta?.business_id,
    kind: 'ps_service',
    kind_label: 'Personal Service',
    id: r.id,
    title: r.title,
    price: Number(r.price_amount),
    duration_min: Number(r.duration_min),
    allow_employee_select: r.allow_employee_select,
    photo_url: r.photo_url || null,
  }, discounts, currency));

  const fromCB = (offerings?.cb_classes || []).map(r => decorateWithDiscounts({
    business_id: meta?.business_id,
    kind: 'cb_class',
    kind_label: 'Class',
    id: r.id,
    title: r.title,
    price: Number(r.price_amount),
    duration_min: Number(r.duration_min),
    capacity: Number(r.capacity),
    photo_url: r.photo_url || null,
  }, discounts, currency));

  // AFTER
  const fromMS = (offerings?.ms_areas || []).map(r => ({
    business_id: meta?.business_id,
    kind: 'ms_table_type',
    kind_label: 'Table',
    id: r.table_type_id,
    ms_seating_config_id: r.seating_config_id,
    ms_area_type: r.area_type,              // KEEP canonical enum for logic
    ms_area_label: r.area_label || r.area_type, // NEW: friendly name
    title: `Table • ${r.area_label || r.area_type}`,
    price: 0,
    photo_url: r.photo_url || null
  }));


  const fromAC = (offerings?.ac_room_types || []).map(r => decorateWithDiscounts({
    business_id: meta?.business_id,
    kind: 'ac_room_type',
    kind_label: 'Room',
    id: r.id,
    title: r.title,
    price: r.base_price_amount != null ? Number(r.base_price_amount) : Number(minSeasonPrice(r.id)),
    quantity: Number(r.quantity),              // NEW: needed for local AC availability
    max_guests: Number(r.max_guests || 0),
    photo_url: r.photo_url || null,
    photos_count: Number(r.photos_count || 0)
  }, discounts, currency));

  // Include bundles (map to a simple offering shape)
  // Note: bundles are treated as standalone purchasable items
  const fromBundles = (offerings?.bundles || []).map(b => decorateWithDiscounts({
    business_id: meta?.business_id,
    kind: 'bundle',
    kind_label: 'Bundle',
    id: b.id,
    title: b.name,
    price: Number(b.price_amount || 0),
    photo_url: b.photo_url || null,
    consumption_window_days: Number(b.consumption_window_days || 1),
    items: b.items || []
  }, discounts, currency));

  return [...fromPS, ...fromCB, ...fromMS, ...fromAC, ...fromBundles];
}

// compute best discount & perks for an offering using bootstrap discounts
function decorateWithDiscounts(offering, discounts, currency) {
  const items = discounts?.items || [];
  const dro = discounts?.reservation_scopes || [];
  const free = discounts?.perks?.free_products || [];
  const disc = discounts?.perks?.discounted_products || [];

  // which discounts apply? (apply_all=true OR scoped explicitly to this offering)
  const applies = items.filter(d => {
    if (d.kind !== 'reservations') return false;
    if (d.apply_all) return true;
    return dro.some(s => s.discount_id === d.id && mapKind(offering.kind) === s.offering_type && s.offering_id === offering.id);
  });

  // pick best (lowest final)
  let best = null;
  applies.forEach(d => {
    const final = d.percentage
      ? Math.max(0, offering.price * (1 - Number(d.value)/100))
      : Math.max(0, offering.price - Number(d.value));
    if (best == null || final < best.final) best = { id: d.id, final };
  });

  const perks = [];
  if (best) {
    free.filter(p => p.discount_id === best.id).forEach(p => perks.push({ type: 'free_product', name: p.name }));
    disc.filter(p => p.discount_id === best.id).forEach(p => perks.push({ type: 'discounted_product', name: p.name, percent: p.percent }));
  }

  return {
    ...offering,
    price_fmt: fmt(offering.price, currency),
    discount: best ? { final: best.final, final_fmt: fmt(best.final, currency) } : null,
    perks
  };
}

function mapKind(k) {
  // maps UI kind to bootstrap discount scope type
  return k; // 'ps_service' | 'cb_class' | 'ms_table_type' | 'ac_room_type' already align
}

function fmt(n, ccy) {
  try { return new Intl.NumberFormat(undefined, { style: 'currency', currency: ccy }).format(n); }
  catch { return `${n?.toFixed?.(2) ?? n} ${ccy}`; }
}

// shape bootstrap.live into { [kind]: Block[] } for the holds utils
function groupLiveByKind(live) {
  const out = { ps_service: [], cb_class: [], ms_table_type: [], ac_room_type: [] };
  const all = [
    ...(live?.reservations || []).map(x => ({ ...x, source: 'reservation' })),
    ...(live?.holds || []).map(x => ({ ...x, source: 'hold' })),
  ];
  all.forEach(x => {
    if (!out[x.offering_kind]) out[x.offering_kind] = [];
    out[x.offering_kind].push(x);
  });
  return out;
}

  // quick map: service_id → [{id,name}]
  function psEmployeesByService(boot) {
    const map = {};
    (boot?.calendars?.ps?.employees || []).forEach(e => {
      const arr = map[e.service_id] || (map[e.service_id] = []);
      arr.push({ id: e.employee_id, name: e.employee_name });
    });
    return map;
  }

  // Step 2 — date whitelist reflects (managed − work) partition per employee
// Step 2 — date whitelist reflects ONLY employee windows (no business-hours requirement)
function psDatesByService(boot) {
  const range = boot?.meta?.booking?.range;
  if (!range?.start_date || !range?.end_date) return {};

  const toMin = (s) => (+s.slice(0,2))*60 + (+s.slice(3,5));
  const toHH  = (m) => String(Math.floor(m/60)).padStart(2,'0')+':'+String(m%60).padStart(2,'0');
  // (subtractWindows & intersectWindows already defined above in this file)

  const empByService = psEmployeesByService(boot);
  const off = new Set();
  (boot?.calendars?.ps?.employee_off_days || []).forEach(r => {
    off.add(`${r.employee_id}|${r.date}`);
  });

  const scheduleModeRows = boot?.calendars?.ps?.schedule_mode || [];
  const modeByService = {};
  scheduleModeRows.forEach(r => { modeByService[r.service_id] = r.mode; });

  const workByEmpDay = new Map();
  (boot?.calendars?.ps?.employee_work_windows || []).forEach(r => {
    const k = `${r.employee_id}|${r.date}`;
    if (!workByEmpDay.has(k)) workByEmpDay.set(k, []);
    workByEmpDay.get(k).push({ start: r.start_time, end: r.end_time });
  });

  // per (service|emp|day) managed rows
  const managedBySvcEmpDay = new Map();
  const managedBySvcDay = new Map(); // NEW: `${service_id}|${date}` -> [{start,end}]
  (boot?.calendars?.ps?.managed_schedule || []).forEach(r => {
    const k = `${r.service_id}|${r.employee_id}|${r.date}`;
    if (!managedBySvcEmpDay.has(k)) managedBySvcEmpDay.set(k, []);
    managedBySvcEmpDay.get(k).push({ start: r.start_time, end: r.end_time });
    const k2 = `${r.service_id}|${r.date}`;
    if (!managedBySvcDay.has(k2)) managedBySvcDay.set(k2, []);
    managedBySvcDay.get(k2).push({ start: r.start_time, end: r.end_time });

  });

  const out = {};
  Object.keys(empByService).forEach(serviceId => {
    const dates = new Set();
    for (let d = new Date(range.start_date); d <= new Date(range.end_date); d.setDate(d.getDate() + 1)) {
      const iso = d.toISOString().slice(0,10);

      const any = (empByService[serviceId] || []).some(e => {
        const eid = e.id;
        if (off.has(`${eid}|${iso}`)) return false;
        const work = workByEmpDay.get(`${eid}|${iso}`) || [];
        const mode = String(modeByService[serviceId] || 'business').toLowerCase();

        if (!work.length) return false; 
        if (mode === 'employee') {
          // availability = managed_this_service_for_emp − employee_work
          const managedThis = managedBySvcEmpDay.get(`${serviceId}|${eid}|${iso}`) || [];
          if (!managedThis.length) return false;
          const usable = subtractWindows(managedThis, work);
          return usable.length > 0;
        } else {
          // BUSINESS: work minus managed windows for OTHER services that day
          const otherManaged = [];
          for (const [k, arr] of managedBySvcEmpDay.entries()) {
            const [svcId, empId, dayIso] = k.split('|');
            if (String(empId) !== String(eid) || dayIso !== iso) continue;
            if (String(svcId) === String(serviceId)) continue; // exclude this service’s own
            otherManaged.push(...arr);
          }
          const usable = subtractWindows(work, otherManaged);
          return usable.length > 0;
        }
      });

      if (any) dates.add(iso);
    }
    out[serviceId] = Array.from(dates).sort();
  });
  return out;
}


  useEffect(() => {
    (async () => {
      const c = await ensureCart(businessId);
      setCart(c);
    })();
  }, [businessId]);

  useEffect(() => {
    if (!cart?.id) return;
    reloadCartItems(cart.id);
  }, [cart?.id]);


  useEffect(() => {
    if (!bootstrap) { setQuote({ subtotal: 0, pay_now: 0, hold_amount: 0, pay_later_total: 0, pay_later_at: null }); return; }
    const q = computeQuote({
      items: cartItems,
      feesCtx: bootstrap?.fees,
      timezone: bootstrap?.meta?.timezone,
      checkInAfter: bootstrap?.meta?.accommodation?.check_in_after || '15:00:00'
    });
    setQuote(q);
  }, [cartItems, bootstrap?.fees, bootstrap?.meta?.timezone, bootstrap?.meta?.accommodation?.check_in_after]);

  useEffect(() => {
    (async () => {
      const boot = await getBootstrap(businessId);
      setBootstrap(boot);

      const uiItems = buildOfferingsFromBootstrap(boot);
      const datesMap = psDatesByService(boot);
      const empMap   = psEmployeesByService(boot);
      // Attach per-offering availableDates for PS immediately; other kinds will be computed on-demand
      let withDatesAndEmps = uiItems.map(o => (
        o.kind === 'ps_service'
          ? { ...o, _availableDates: datesMap[o.id] || [], _employees: empMap[o.id] || [] }
          : o
      ));

      // Helper: compute available dates for a single offering (used for bundle items)
      const computeDatesForOffering = (off) => {
        const range = boot?.meta?.booking?.range;
        if (!range?.start_date || !range?.end_date) return [];
        const out = new Set();
        const start = new Date(range.start_date);
        const end = new Date(range.end_date);

        if (off.kind === 'ps_service') {
          return datesMap[off.id] || [];
        }

        if (off.kind === 'cb_class') {
          for (let d = new Date(start); d <= end; d.setDate(d.getDate()+1)) {
            const iso = d.toISOString().slice(0,10);
            const r = cbSessionsLocal({ dateISO: iso, classId: off.id, capacity: off.capacity, cbSessions: boot?.calendars?.cb_sessions || [], tz: boot?.meta?.timezone, firstBookableISO: boot?.meta?.booking?.first_bookable_ts, live: liveWithCart });
            if ((r.sessions||[]).some(s => s.remaining >= 1)) out.add(iso);
          }
          return Array.from(out).sort();
        }

        if (off.kind === 'ms_table_type') {
          for (let d = new Date(start); d <= end; d.setDate(d.getDate()+1)) {
            const iso = d.toISOString().slice(0,10);
            const any = msSlotsClient({ dateISO: iso, party: 1, seatingConfigId: off.ms_seating_config_id, areaType: off.ms_area_type, hoursCtx: boot?.calendars?.hours, msRules: boot?.calendars?.ms_rules, live: liveWithCart, firstBookableISO: boot?.meta?.booking?.first_bookable_ts, tz: boot?.meta?.timezone }).length > 0;
            if (any) out.add(iso);
          }
          return Array.from(out).sort();
        }

        if (off.kind === 'ac_room_type') {
          // treat any priced season day as available for the room
          const seasons = boot?.calendars?.ac_pricing?.seasons || [];
          for (let d = new Date(start); d <= end; d.setDate(d.getDate()+1)) {
            const iso = d.toISOString().slice(0,10);
            const any = seasons.filter(s => String(s.room_type_id) === String(off.id)).some(s => {
              const a = new Date(s.start_date), b = new Date(s.end_date);
              return (new Date(iso) >= a && new Date(iso) <= b);
            });
            if (any) out.add(iso);
          }
          return Array.from(out).sort();
        }

        return [];
      };

      // Compute bundle availability: window-based rule
      const offeringsByKindId = {};
      withDatesAndEmps.forEach(o => { offeringsByKindId[`${o.kind}|${o.id}`] = o; });
      const bundles = withDatesAndEmps.filter(o => o.kind === 'bundle');
      for (const b of bundles) {
        // b.items are objects {mode, item_id, title} as returned by server
        const perItemDates = (b.items || []).map(it => {
          // map mode to offering kind and id field
          if (it.mode === 'personal_services') {
            const off = withDatesAndEmps.find(x => x.kind === 'ps_service' && String(x.id) === String(it.item_id));
            return off ? computeDatesForOffering(off) : [];
          }
          if (it.mode === 'class_based') {
            const off = withDatesAndEmps.find(x => x.kind === 'cb_class' && String(x.id) === String(it.item_id));
            return off ? computeDatesForOffering(off) : [];
          }
          if (it.mode === 'modular_seating') {
            // item_id may be table_type id — try to find matching ms_table_type offering
            const off = withDatesAndEmps.find(x => x.kind === 'ms_table_type' && String(x.id) === String(it.item_id));
            return off ? computeDatesForOffering(off) : [];
          }
          if (it.mode === 'accommodation') {
            const off = withDatesAndEmps.find(x => x.kind === 'ac_room_type' && String(x.id) === String(it.item_id));
            return off ? computeDatesForOffering(off) : [];
          }
          return [];
        });

        // window logic: candidate start can be ANY day in booking range,
        // not necessarily a day when an item is available. This lets a window
        // begin on a date T where items are consumable on T..T+N-1.
        const range = boot?.meta?.booking?.range;
        const winDays = Math.max(1, Number(b.consumption_window_days || 2));
        const validStarts = [];
        if (range?.start_date && range?.end_date) {
          for (let d = new Date(range.start_date); d <= new Date(range.end_date); d.setDate(d.getDate()+1)) {
            const startISO = d.toISOString().slice(0,10);
            const startDate = new Date(startISO);
            const endDate = new Date(startDate);
            endDate.setDate(endDate.getDate() + (winDays - 1));

            const withinWin = (iso) => {
              const td = new Date(iso);
              return td >= startDate && td <= endDate;
            };

            let ok = true;
            for (const arr of perItemDates) {
              // For AC we treat perItemDates as the list of season-priced days.
              // Requirement: the user's AC stay just needs at least one day inside the window.
              if (!(arr || []).some(withinWin)) { ok = false; break; }
            }
            if (ok) validStarts.push(startISO);
          }
        }
        b._availableDates = Array.from(new Set(validStarts)).sort();
      }

      // merge back bundles into withDatesAndEmps
      withDatesAndEmps = withDatesAndEmps.map(o => {
        if (o.kind === 'bundle') {
          const b = bundles.find(x => String(x.id) === String(o.id));
          return { ...o, _availableDates: (b && b._availableDates) || [] };
        }
        return o;
      });
      setOfferings(withDatesAndEmps);
      // 2) seed live holds/reservations into socket state
      setSeedLive(groupLiveByKind(boot?.live));

      // 3) preload preorder payload (no extra call)
      setPreorder(prev => ({
        ...prev,
        allow: !!boot?.preorder?.allow,
        products: boot?.preorder?.products || []
      }));
    })();
  }, [businessId]);

async function handleAddToCart(o, pick) {
  if (!cart) return;

  const ensureEndFromStart = (startIso, minutes) => {
    try {
      const [datePart, timePart] = String(startIso).split('T');
      const [h, m, s='00'] = (timePart || '').split(':').map(Number);
      if (!Number.isFinite(h) || !Number.isFinite(m)) return null;
      let total = h*60 + m + Number(minutes || 0);
      let day = datePart;
      if (total >= 1440) {
        total -= 1440;
        const d = new Date(datePart);
        d.setDate(d.getDate() + 1);
        day = d.toISOString().slice(0,10);
      }
      const hh = String(Math.floor(total/60)).padStart(2,'0');
      const mm = String(total%60).padStart(2,'0');
      return `${day}T${hh}:${mm}:${String(s||0).padStart(2,'0')}`;
    } catch { return null; }
  };

  // Build payload by kind
  let payload;

  const isDup = (p) => {
    const keyFor = (it) => {
      const kind = String(it.offering_kind);
      const id = String(it.offering_id);

      // Prefer the exact local strings we store on add (stable/no TZ shifts)
      const startLocal = it.guests_json?.slot_start_local
        || (typeof it.slot_start === 'string' ? it.slot_start.slice(0, 16) : '');
      const endLocal = it.guests_json?.slot_end_local
        || (typeof it.slot_end === 'string' ? it.slot_end.slice(0, 16) : '');

      if (kind === 'ac_room_type') {
        const s = it.guests_json?.ac_start_date || '';
        const e = it.guests_json?.ac_end_date || '';
        // include a quantity token so user can add multiple identical ranges
        const qty = Number(it.guests_json?.party_size || 1);
        return `${kind}|${id}|${s}|${e}|qty:${qty}`;
      }

      if (kind === 'ps_service') {
        const emp = it.employee_id || '';
        // same service + same time + same employee = same selection
        return `${kind}|${id}|${startLocal}|${endLocal}|emp:${emp}`;
      }

      if (kind === 'cb_class') {
        const sid = it.guests_json?.class_session_id || '';
        const sdate = it.guests_json?.class_session_date || '';
        // class identity comes from the session id + date
        return `${kind}|${id}|${sid}|${sdate}`;
      }

      if (kind === 'ms_table_type') {
        const sc = it.guests_json?.ms_seating_config_id || '';
        const area = it.guests_json?.ms_area_type || '';
        // same area + same time window = same selection
        return `${kind}|${id}|${startLocal}|${endLocal}|sc:${sc}|area:${area}`;
      }

      // fallback
      return `${kind}|${id}|${startLocal}|${endLocal}`;
    };

    const newKey = keyFor({
      offering_kind: p.offering_kind,
      offering_id: p.offering_id,
      slot_start: p.slot_start,
      slot_end: p.slot_end,
      employee_id: p.employee_id,
      guests_json: p.guests_json,
    });

    return cartItems.some((it) => keyFor(it) === newKey);
  };


  if (o.kind === 'bundle') {
    const items = pick?.guests_json?.bundle_items || [];
    if (!Array.isArray(items) || items.length === 0) {
      alert('Please pick times/dates for the bundle items first.');
      return;
    }
    payload = {
      offering_id: o.id,
      offering_kind: 'bundle',
      slot_start: null,
      slot_end: null,
      employee_id: null,
      guests_json: {
        party_size: pick?.party || applied.party,
        bundle_start_date: pick?.guests_json?.bundle_start_date || null,
        bundle_items: items,
      },
      add_ons_json: [],
      policy_snapshot_json: pick?.policy_snapshot_json || {},
      base_price_preview: pick?.price ?? o.price,
    };
    if (isDup(payload)) { alert('That selection is already in your cart.'); return; }
  } else if (o.kind === 'ac_room_type') {
    const acStart = pick?.guests_json?.ac_start_date;
    const acEnd   = pick?.guests_json?.ac_end_date;
    if (!acStart || !acEnd) {
      alert('Please pick check-in and check-out.');
      return;
    }
    payload = {
      offering_id: o.id,
      offering_kind: 'ac_room_type',
      slot_start: null,
      slot_end: null,
      employee_id: null,
      guests_json: {
        ac_start_date: acStart,
        ac_end_date: acEnd,
        party_size: Math.max(1, Number(pick?.party || pick?.guests_json?.party_size || 1)),
        ...(pick?.guests_json || {})
      },
      add_ons_json: [],
      policy_snapshot_json: pick?.policy_snapshot_json || {},
      base_price_preview: pick?.price ?? o.price,
    };
    if (isDup(payload)) { alert('That selection is already in your cart.'); return; }
  } else {
    // Non-AC must include time
    let slotStart = pick.slot_start || null;
    let slotEnd   = pick.slot_end   || null;
    if (slotStart && !slotEnd) {
      slotEnd = ensureEndFromStart(slotStart, o.duration_min || 0) || null;
    }
    if (!slotStart || !slotEnd) {
      alert('Please pick a valid time.');
      return;
    }

    payload = {
      offering_id: o.id,
      offering_kind: o.kind,
      slot_start: slotStart,
      slot_end: slotEnd,
      employee_id: pick.employee_id || null,
      guests_json: {
        party_size: pick.party || applied.party,
        class_session_id: pick.class_session_id || null,
        class_session_date: pick.class_session_date || null,
        ms_seating_config_id: pick.ms_seating_config_id || null,
        ms_area_type: pick.ms_area_type || null,
        slot_start_local: slotStart,
        slot_end_local: slotEnd,
        // per-offering options captured in the inline picker (future backend use)
        ...(pick?.guests_json || {})
      },
      add_ons_json: [],
      policy_snapshot_json: pick.policy_snapshot_json || {},
      base_price_preview: pick.price ?? o.price,
    };
    if (isDup(payload)) { alert('That slot is already in your cart.'); return; }
  }

  // Build a stable "pending" key to dedupe click-spam until the cart refreshes.
  const pendingKey = (() => {
    const startLocal = payload.guests_json?.slot_start_local || payload.slot_start || '';
    const endLocal   = payload.guests_json?.slot_end_local   || payload.slot_end   || '';
    if (payload.offering_kind === 'ac_room_type') {
      const s = payload.guests_json?.ac_start_date || '';
      const e = payload.guests_json?.ac_end_date || '';
      return `ac|${payload.offering_id}|${s}|${e}`;
    }
    return `${payload.offering_kind}|${payload.offering_id}|${startLocal}|${endLocal}|emp:${payload.employee_id||''}|sc:${payload.guests_json?.ms_seating_config_id||''}|area:${payload.guests_json?.ms_area_type||''}`;
  })();
  if (pendingAddsRef.current.has(pendingKey)) return;
  pendingAddsRef.current.add(pendingKey);
  try {
    await addItem(cart.id, payload);
    await reloadCartItems(cart.id);
  } finally {
    pendingAddsRef.current.delete(pendingKey);
  }
}


  // Preorder conditionals
  const hasMS = useMemo(() => cartItems.some(it => it.offering_kind === 'ms_table_type'), [cartItems]);
  const hasPSorCB = useMemo(
    () => cartItems.some(it => it.offering_kind === 'ps_service' || it.offering_kind === 'cb_class'),
    [cartItems]
  );
  const hasTimeBased = useMemo(() => cartItems.some(it => it.offering_kind !== 'ac_room_type'), [cartItems]);

  useEffect(() => {
    if (!hasMS) { setPreorder(p => ({ ...p, allow: false })); return; }
    if (bootstrap?.preorder) {
      setPreorder(prev => ({ ...prev, allow: !!bootstrap.preorder.allow }));
    }
  }, [hasMS, bootstrap]);

  function onApplyTimeFilters() {
    setApplied({ ...queryDraft });
    setTab('time');
  }
console.log(applied);
  async function handleCheckout() {
    if (!cart) return;

    // 1) Place slot holds
    let ttlMin = 2;
    try {
      const resp = await createHolds(businessId, cart.id);
      if (resp && typeof resp.ttl_min === 'number') ttlMin = resp.ttl_min;
    }
    catch (e) {
      if (e?.response?.status === 409) return alert('One or more selections were just taken. Please adjust times.');
      throw e;
    }

    // 2) Navigate to embedded checkout page with cart_id
    const t0 = Date.now();
    const ttlSec = Math.max(0, Math.floor((ttlMin || 2) * 60));
    navigate(`/reserve/${businessId}/checkout?cart_id=${encodeURIComponent(cart.id)}&ttl=${ttlSec}&t0=${t0}`);
  }


  return (
    <div className="max-w-6xl mx-auto p-4">
      {expiredMsg && (
        <div className="mb-3 p-3 rounded bg-yellow-50 border border-yellow-200 text-yellow-800 text-sm">
          Your checkout session expired. Please pick times again and retry.
        </div>
      )}
      {/* Tabs */}
      <div className="flex items-center gap-2 border-b mb-4">
        {['offerings','time'].map(t => (
          <button
            key={t}
            onClick={() => setTab(t)}
            className={`px-4 py-2 -mb-px border-b-2 ${tab===t ? 'border-black font-semibold' : 'border-transparent text-gray-500'}`}>
            {t === 'offerings' ? 'Pick Offerings' : 'Pick Time'}
          </button>
        ))}
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-[1fr_320px] gap-6">
        <div>
          {/* Show the time filters ONLY on the Pick Time tab */}
          {tab === 'time' && (
            <TimeFiltersBar
              draft={queryDraft}
              setDraft={setQueryDraft}
              onApply={onApplyTimeFilters}
              bookingRange={bootstrap?.meta?.booking?.range}
              includeDates={includeDatesForTimeTab}
            />
          )}

          {tab === 'offerings' && (
            <div className="mt-4 grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
              {offerings.map(o => (
                <OfferingCard
                  key={`${o.kind}-${o.id}`}
                  offering={{
                    ...o,
                    _employees: o.kind === 'ps_service'
                      ? (psEmployeesByService(bootstrap)?.[o.id] || [])
                      : []
                  }}
                  currency={bootstrap?.meta?.base_currency || 'EUR'}
                  feesCtx={bootstrap?.fees}
                  editCtx={bootstrap?.edit_rules}
                  preorder={preorder}
                  setPreorder={setPreorder}
                  bookingRange={bootstrap?.meta?.booking?.range}
                  timezone={bootstrap?.meta?.timezone}
                  firstBookableTs={bootstrap?.meta?.booking?.first_bookable_ts}
                  onPick={(offering, pick) => handleAddToCart(offering, pick)}
                  live={liveWithCart}
                  hoursCtx={bootstrap?.calendars?.hours}
                  offeringsIndex={Object.fromEntries((offerings||[]).map(x => [x.id, x]))}
                  psSchedule={bootstrap?.calendars?.ps?.managed_schedule || []}
                  psActivePeriods={bootstrap?.calendars?.ps?.service_active_periods || []}
                  psEquipPools={bootstrap?.calendars?.ps?.equipment_pools || []}
                  psEquipUsage={bootstrap?.calendars?.ps?.service_equipment_usage || []}
                  cbSessions={bootstrap?.calendars?.cb_sessions || []}
                  acSeasons={bootstrap?.calendars?.ac_pricing?.seasons || []}
                  acModifiers={bootstrap?.calendars?.ac_pricing?.modifiers || []}
                  msRules={bootstrap?.calendars?.ms_rules}
                  employeesIndex={employeeIndex}
                  psScheduleMode={psScheduleMode}
                  psEmpWork={bootstrap?.calendars?.ps?.employee_work_windows || []}
                  psOffDays={bootstrap?.calendars?.ps?.employee_off_days || []}
                />
              ))}
              {!offerings.length && (
                <div className="text-sm text-gray-500">No offerings found.</div>
              )}
            </div>
          )}

          {tab === 'time' && (
            <TimeResultsGrid
              offerings={offerings}
              filters={applied} // includes day_from/day_to/win_start/win_end/party/category/ac_exact
              bookingRange={bootstrap?.meta?.booking?.range}
              timezone={bootstrap?.meta?.timezone}
              firstBookableTs={bootstrap?.meta?.booking?.first_bookable_ts}
              hoursCtx={bootstrap?.calendars?.hours}
              psSchedule={bootstrap?.calendars?.ps?.managed_schedule || []}
              psEmpWork={bootstrap?.calendars?.ps?.employee_work_windows || []}
              psOffDays={bootstrap?.calendars?.ps?.employee_off_days || []}
              psScheduleMode={psScheduleMode}
              psActivePeriods={bootstrap?.calendars?.ps?.service_active_periods || []}
              psEquipPools={bootstrap?.calendars?.ps?.equipment_pools || []}
              psEquipUsage={bootstrap?.calendars?.ps?.service_equipment_usage || []}
              cbSessions={bootstrap?.calendars?.cb_sessions || []}
              acSeasons={bootstrap?.calendars?.ac_pricing?.seasons || []}
              acModifiers={bootstrap?.calendars?.ac_pricing?.modifiers || []}
              msRules={bootstrap?.calendars?.ms_rules}
              live={liveWithCart}
              feesCtx={bootstrap?.fees}
              editCtx={bootstrap?.edit_rules}
              currency={bootstrap?.meta?.base_currency || 'EUR'}
              employeeIndex={employeeIndex}
              preorder={preorder}
              setPreorder={setPreorder}
              onPick={(offering, pick) => handleAddToCart(offering, pick)}
            />
          )}
        </div>
        <div className="lg:sticky lg:top-4 h-fit">
          <CartSummary
            cart={cart}
            items={cartItems}
            quote={quote}
            onRefresh={() => { reloadCartItems(); }}
            onCheckout={handleCheckout}
          />
        </div>
      </div>
    </div>
  );
}
